import { FeatureDetectionRef } from "@/components/common/feature-detection-context";
import { getPickedPoint } from "@faro-lotv/app-component-toolbox";
import type { GUID } from "@faro-lotv/foundation";
import { isCadModel, PointCloud, TypedEvent } from "@faro-lotv/lotv";
import {
  DomEvent,
  ThreeEvent,
} from "@react-three/fiber/dist/declarations/src/core/events";
import { EventDispatcher, MOUSE, Vector2, Vector3 } from "three";
import { ToolControlsLogic } from "../tool-controls-interface";

/**
 * Define the way the next picked point should be projected on the previous picked point.
 */
export enum ProjectionType {
  // Do not project the point = the new point will be the same as the picked point
  DoNotProject = "DoNotProject",
  // Project to either the horizontal plane or vertical line from latest point (whichever is closer)
  ProjectOnHorizontalOrVertical = "ProjectOnHorizontalOrVertical",
  // Project to either the line from latest point parallel to X or Y axis, never on Z (whichever is closer)
  ProjectOnXY = "ProjectOnXY",
}

export type MeasurementCompletedEvent = {
  /** true if the measurement is a closed polygon; false otherwise */
  isClosed: boolean;

  /** id of the IElement used for measurement */
  iElementId: string;

  /** true if polygon is closed by click on first point */
  isCompletedByClickFirstPoint?: boolean;

  /** true indicating the horizontal/vertical filter was used during measurement */
  useHorizontalVerticalFilter?: boolean;

  /** true indicating the edge/corner extraction was used during measurement */
  useEdgeCornerExtraction?: boolean;
};

/**
 * Provides functionality for taking a polygon measurement in 3D.
 * During the measurement process, events are emitted to indicate when a measurement has started,
 * when the picked points have changed, and when a measurement has been completed.
 *
 * TODO: add tests https://faro01.atlassian.net/browse/SWEB-3911
 */
export class MultiPointMeasureControlsLogic
  extends EventDispatcher
  implements ToolControlsLogic
{
  // Current list of the points in the measurement
  #points: Vector3[] | undefined = [];

  // The "current" point = either the latest picked point (even if this point is not accepted in the measurement)
  // or the currently hovered existing point in the measurement
  #currentPoint = new Vector3();

  // The "current" point before optional projection.
  #rawCurrentPoint = new Vector3();

  // The coordinate on screen in pixels of the mouse pointer hovering a point (could be slightly different from the point in measurement)
  #endPointInPixels = new Vector2();

  // The id of the IElement that is currently being measured
  // It is same for all picked points except for the case of mixed point cloud and cad model.
  // In case of mixed point cloud and cad model, this id is always the IElement id of the point cloud.
  #currentElement: string | undefined;

  // Define whether we should project the point, and how to project it.
  #projectionType: ProjectionType = ProjectionType.DoNotProject;

  // Flag indicating the horizontal/vertical filter was used during measurement
  #useHorizontalVerticalFilter: boolean = false;

  // Flag indicating the edge/corner extraction was used during measurement
  #useEdgeCornerExtraction: boolean = false;

  // Points removed with deleteLatestPoint, most recent last, that can be restored with redoLatestPoint
  #undonePoints: Array<{ point: Vector3; element?: string }> = [];

  /**
   * Enable or disable the projection of the new point to latest point.
   *
   * @param projectionType ProjectionType defining the way to project the mouse pointer on previous picked point,
   *  ProjectionType.DoNotProject to disable projection
   */
  enableProjection(projectionType: ProjectionType): void {
    const valueChanged = this.#projectionType !== projectionType;
    this.#projectionType = projectionType;
    if (valueChanged) {
      this.#computeCurrentPoint();
      this.onCurrentPointChanged.emit(this.#currentPoint);
    }
  }

  // Called after first point was picked
  onMeasurementStarted = new TypedEvent<void>();

  // Called when the whole measurement is completed
  onMeasurementCompleted = new TypedEvent<MeasurementCompletedEvent>();

  // Called when the whole measurement is canceled
  onMeasurementCanceled = new TypedEvent<void>();

  // Called whenever the current point changes
  //  Parameter is the new coordinate of the current point, undefined when there is no current point anymore
  onCurrentPointChanged = new TypedEvent<Vector3 | undefined>();

  // Called whenever the list of points changed (i.e. a point was added or removed, of the whole list flushed )
  onPointsChanged = new TypedEvent<void>();

  /**
   * Project the point to either the horizontal plane or vertical line from referencePoint (whichever is closer)
   *
   * @param point The point to project
   * @param referencePoint the reference point to project the point to
   * @returns The projected point
   */
  #projectPointOnHorizontalOrVertical(
    point: Vector3,
    referencePoint: Vector3,
  ): Vector3 {
    const verticalDeviation = Math.abs(point.y - referencePoint.y);
    const horizontalDeviation = Math.sqrt(
      Math.pow(point.x - referencePoint.x, 2) +
        Math.pow(point.z - referencePoint.z, 2),
    );
    if (verticalDeviation < horizontalDeviation) {
      // new point is closer from previous horizontal plane that vertical line = project on the horizontal plane
      return new Vector3(point.x, referencePoint.y, point.z);
    }
    // new point is closer from previous vertical line than horizontal plane = project on the vertical line
    return new Vector3(referencePoint.x, point.y, referencePoint.z);
  }

  /**
   * Project the point to either X or Y user axis, not Z, passing by a specific reference point (whichever is closer)
   *
   * @param point The point to project
   * @param referencePoint the reference point to project the point to
   * @returns The projected point
   */
  #projectPointOnXY(point: Vector3, referencePoint: Vector3): Vector3 {
    // User's Z axis is indeed Y internal axis
    const xDeviation = Math.abs(point.x - referencePoint.x);
    const zDeviation = Math.abs(point.z - referencePoint.z);
    if (xDeviation < zDeviation) {
      // new point is closer from x axis
      return new Vector3(referencePoint.x, point.y, point.z);
    }
    // new point is closer from z axis
    return new Vector3(point.x, point.y, referencePoint.z);
  }

  /**
   * Project the point according to active option.
   *
   * @param point The point to project
   * @param referencePoint the reference point to project the point to
   * @returns The projected point
   */
  #projectPoint(point: Vector3, referencePoint: Vector3): Vector3 {
    switch (this.#projectionType) {
      case ProjectionType.ProjectOnHorizontalOrVertical:
        return this.#projectPointOnHorizontalOrVertical(point, referencePoint);
      case ProjectionType.ProjectOnXY:
        return this.#projectPointOnXY(point, referencePoint);
      default:
        throw new Error("Invalid projection type");
    }
  }

  #computeRawCurrentPoint(
    ev: ThreeEvent<DomEvent>,
    features?: FeatureDetectionRef,
    onClick?: boolean,
  ): void {
    getPickedPoint(ev, this.#rawCurrentPoint);

    if ((ev.metaKey || ev.ctrlKey) && features) {
      if (features.corner.current) {
        this.#rawCurrentPoint.copy(features.corner.current);
        if (onClick) this.#useEdgeCornerExtraction = true;
      } else if (features.edge.current) {
        this.#rawCurrentPoint.copy(features.edge.current.point);
        if (onClick) this.#useEdgeCornerExtraction = true;
      }
    }
  }

  /**
   * Update currentPoint form current value of rawCurrentPoint.
   *
   * @param onClick True if the current point is computed from a click event, false otherwise
   */
  #computeCurrentPoint(onClick?: boolean): void {
    if (
      this.#points?.length &&
      this.#projectionType !== ProjectionType.DoNotProject
    ) {
      this.#currentPoint.copy(
        this.#projectPoint(
          this.#rawCurrentPoint,
          this.#points[this.#points.length - 1],
        ),
      );
      if (onClick) {
        this.#useHorizontalVerticalFilter = true;
      }
    } else {
      this.#currentPoint.copy(this.#rawCurrentPoint);
    }
  }

  /**
   * Called when the user moves the mouse above the current active model
   *
   * @param ev The mouse event that triggered this callback
   * @param iElementId The iElement that triggered this event
   * @param features The feature detection data
   */
  pointHovered(
    ev: ThreeEvent<DomEvent>,
    iElementId: string,
    features?: FeatureDetectionRef,
  ): void {
    if (ev.object instanceof PointCloud || isCadModel(ev.object)) {
      ev.stopPropagation();
    }

    this.#computeRawCurrentPoint(ev, features);
    this.#computeCurrentPoint();
    this.#endPointInPixels.set(ev.clientX, ev.clientY);
    this.onCurrentPointChanged.emit(this.#currentPoint);
  }

  /**
   * Called when the user clicks/taps on the current active model
   *
   * @param ev The mouse event that triggered this callback
   * @param iElementId The iElement that triggered this event
   * @param features The feature detection data
   */
  pointClicked(
    ev: ThreeEvent<DomEvent>,
    iElementId: string,
    features?: FeatureDetectionRef,
  ): void {
    if (ev.button !== MOUSE.LEFT) return;

    // stop propagation of the event to avoid adding twice the same points when 2 or more objects/sheets are pick-able
    ev.stopPropagation();

    this.#computeRawCurrentPoint(ev, features, true);
    this.forcePointClick(iElementId, ev.object instanceof PointCloud);
  }

  /**
   * Considers the last point hovered as clicked.
   *
   * @param iElementId id of the iElement being clicked
   * @param isPointCloud whether a point cloud is being clicked
   */
  forcePointClick(iElementId: GUID, isPointCloud: boolean = true): void {
    if (!this.#points) this.#points = [];
    this.#computeCurrentPoint(true);
    if (
      this.#points.length > 0 &&
      this.#currentPoint.distanceTo(this.#points[this.#points.length - 1]) <
        Number.EPSILON
    ) {
      return;
    }
    this.#points.push(this.#currentPoint.clone());
    this.#undonePoints = [];
    if (isPointCloud) {
      // always take the point cloud element as the current element
      this.#currentElement = iElementId;
    }
    this.onPointsChanged.emit();

    if (this.#points.length === 1) {
      this.onMeasurementStarted.emit();
      this.#currentElement = iElementId;
    }
  }

  /**
   * Complete the interaction
   *
   * @param isClosed True if the measurement is a closed polygon
   * @param isCompletedByClickFirstPoint if true, polygon is closed by click on first point
   */
  complete(isClosed: boolean, isCompletedByClickFirstPoint?: boolean): void {
    if (!this.#currentElement) return;
    this.onMeasurementCompleted.emit({
      isClosed,
      iElementId: this.#currentElement,
      isCompletedByClickFirstPoint,
      useHorizontalVerticalFilter: this.#useHorizontalVerticalFilter,
      useEdgeCornerExtraction: this.#useEdgeCornerExtraction,
    });
    this.#points = undefined;
    this.#undonePoints = [];
    this.onPointsChanged.emit();
    this.onCurrentPointChanged.emit(undefined);
    this.#currentElement = undefined;
    this.#useHorizontalVerticalFilter = false;
    this.#useEdgeCornerExtraction = false;
  }

  /**
   * Remove one point from the list of picked points
   *
   * @param index The index of the point to remove
   * @param closeCommandOnLastPoint True if the measurement should be canceled when the last point is removed
   */
  deletePoint(index: number, closeCommandOnLastPoint: boolean): void {
    if (!this.#points) return;
    if (index < 0 || index >= this.#points.length) return;

    this.#points.splice(index, 1);
    this.onPointsChanged.emit();

    if (this.#points.length === 0 && closeCommandOnLastPoint) {
      this.cancel();
    }
  }

  /**
   * Delete last point in the list of picked points if there is any.
   *
   * @returns True if a point was deleted, false otherwise
   */
  deleteLatestPoint(): boolean {
    if (!this.#points || this.#points.length === 0) return false;

    this.#undonePoints.push({
      point: this.#points[this.#points.length - 1],
      element: this.#currentElement,
    });
    this.deletePoint(this.#points.length - 1, false);

    if (this.#points.length === 0) {
      this.#currentElement = undefined;
    }

    return true;
  }

  /**
   * Restore the latest point removed with deleteLatestPoint, if no point was picked since then.
   *
   * @returns True if a point was restored, false otherwise
   */
  redoLatestPoint(): boolean {
    const undone = this.#undonePoints.pop();
    if (!undone) return false;

    if (!this.#points) this.#points = [];
    this.#points.push(undone.point);
    this.onPointsChanged.emit();

    if (this.#points.length === 1) {
      this.#currentElement = undone.element;
      this.onMeasurementStarted.emit();
    }
    return true;
  }

  /**
   * @returns True if there are removed points that can be restored with redoLatestPoint
   */
  canRedo(): boolean {
    return this.#undonePoints.length > 0;
  }

  /**
   * Initialize the measurement
   */
  initialize(): void {
    this.#points = undefined;
    this.#undonePoints = [];
    this.onPointsChanged.emit();
    this.onCurrentPointChanged.emit(undefined);
    this.#currentElement = undefined;
  }

  /**
   * Cancels the measurement
   */
  cancel(): void {
    this.initialize();
    this.onMeasurementCanceled.emit();
  }

  /**
   * @returns The list of picked points. Undefined if the measurement has never started
   */
  get points(): Vector3[] | undefined {
    return this.#points;
  }

  /**
   * @returns true if the measurement is started or live
   */
  isLiveMeasure(): boolean {
    return !!this.#points;
  }

  /**
   * @returns The 2D coordinates in pixels of the point on which the mouse hovering
   */
  get pointCoordinates(): Vector2 {
    return this.#endPointInPixels;
  }
}
